import React, { useCallback, useRef, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { extend } from '@thd-nucleus/data-sources';
import { ExperienceContext, useConfigService } from '@thd-nucleus/experience-context';
import {
  ProductPod,
  PodFooter,
  PodHeader,
  PodSection,
  PodSpacer,
  PodRowToColumn,
  PodRow,
  PodTapper,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductServiceAddOns,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductImage,
  BundleDataModel,
} from '@thd-olt-component-react/product-pod';
import { AddToList } from '@thd-olt-component-react/add-to-list';
import { FulfillmentPodShipping, FulfillmentPodStore } from '@thd-olt-component-react/fulfillment';
import { Price, PriceClearanceDataModel } from '@thd-olt-component-react/price';
import { Promotion } from '@thd-olt-component-react/promotion';
import { ProductHeader } from '@thd-olt-component-react/product-details';
import { SuperSkuMiniSwatches } from '@thd-olt-component-react/super-sku';
import {
  ProductSeeSimilarItems,
  ViewSimilarIcon,
} from '@thd-olt-component-react/product-pod-group';
import { NonBuyableQuoteCenter } from '@thd-olt-component-react/non-buyable-quote-center';
import { OrderSample } from '@thd-olt-component-react/order-sample';
import { AisleBayPod } from '@thd-olt-component-react/aisle-bay';
import { ProductQuantity } from '@thd-olt-component-react/quantity';
import { sendPodTapperAnalytics } from '@thd-olt-component-react/product-pod/dist/components/productPodUtils';
import { useClickInteractionListener } from '@thd-olt-functional/utils';
import { AddToVariations } from './AddToVariations';

export const CustomProductPodResponsiveB2B = ({
  itemId,
  parentId,
  storeId,
  position,
  hoverDelay,
  onClick,
  url,
  samplesLoading,
  itemRecsIndex,
  hasInStoreFilter,
  parent,
  impProductPodData,
  hasSameDayFilter
}) => {
  const isFulfillmentFilterAndMsgEnabled = useConfigService('fs:isFulfillmentFilterAndMsgEnabled');
  const isViewSimilarButtonEnabled = useConfigService('fs:isViewSimilarButtonPositionAboveABTest');

  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const isMobileOrConsumerApp = isConsumerApp || channel === 'mobile';
  const superSkuChangedItemIdsRef = useRef([]);
  const showFulfillmentPodStore = isFulfillmentFilterAndMsgEnabled ? !hasSameDayFilter : true;
  const showFulfillmentPodShipping = isFulfillmentFilterAndMsgEnabled ? !hasInStoreFilter : true;

  // -----------------
  // Analytics - New-Tab Click Tracker.
  // -----------------

  const productClickData = {
    podAction: 'product pod',
    podAnchorSku: itemId,
    target: '_self',
    parent: 'plp',
  };

  const sendAnalytics = () => {
    if (typeof window === 'undefined') return;
    window.LIFE_CYCLE_EVENT_BUS?.trigger('product-pod-v7.click', productClickData); // analytic & ad-server
  };

  const useNodeRef = () => {
    const [node, setNode] = useState(null); // <HTMLElement | null>

    /**
     * Callback ref to be called by React whenever the ref gets attached
     * to a different node or becomes null.
     * @see https://legacy.reactjs.org/docs/hooks-faq.html#how-can-i-measure-a-dom-node
     */
    const callbackRef = useCallback((value) => {
      setNode(value);
    }, []);

    return [callbackRef, node];
  };

  const [productPodNodeRef, productPodNode] = useNodeRef();

  useClickInteractionListener({
    callbackFn: sendAnalytics,
    targetNode: productPodNode,
    itemId,
    sponsoredId: itemId,
    trackSource: '',
    slotId: '',
    position,
    plaLocation: '',
  });

  return (
    <div
      ref={productPodNodeRef}
      data-product-id={itemId}
      className="product-pod sui-h-full"
      data-testid="product-pod-b2b"
    >
      <ProductPod
        analyticsData={{ parent, position }}
        analyticImpressionData={impProductPodData}
        hasSSKU
        hoverDelay={hoverDelay}
        itemId={itemId}
        storeId={storeId}
        padding="sui-p-2 sm:sui-pt-2 sm:sui-px-4 sm:sui-pb-4"
        render={(pod) => (
          <>
            <a
              className="sui-top-0 sui-left-0 sui-absolute sui-size-full sui-z-10"
              href={url}
              aria-label="Link"
            />
            <div className="sui-relative sui-flex sui-flex-col sui-h-full">
              <PodSpacer padding={['BOTTOM']} minHeight="32px">
                <PodHeader noPadding>
                  <PodRow noPadding>
                    <ProductBadge itemId={pod.itemId} storeId={storeId} />
                    <ProductSponsored itemId={pod.itemId} position={position} />
                  </PodRow>
                </PodHeader>
              </PodSpacer>
              <PodSection id="Slot-2" display="sui-grid" gap="sui-gap-0" noPadding>
                <PodRowToColumn noPadding gap="sui-gap-x-4 md:sui-gap-x-0">
                  <PodTapper zIndex>
                    <ProductImage
                      itemId={pod.itemId}
                      showSecondaryImage={false}
                      dualImageSwipe={false}
                      onClick={onClick}
                    />
                  </PodTapper>
                  <PodSection
                    display="sui-block sui-w-full"
                    noPadding
                    padding="sui-pt-2"
                    grid="sui-col-start-1 sui-col-end-3 md:sui-order-2"
                    order="sui-order-3"
                  >
                    <PodTapper
                      zIndex
                      aPointer
                      buttonPointer
                      cursorPointer
                      handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                    >
                      <ProductSample itemId={pod.itemId} />
                      <SuperSkuMiniSwatches
                        storeId={storeId}
                        itemId={pod.itemId}
                        onChange={(clickData) => {
                          if (!superSkuChangedItemIdsRef.current.includes(clickData.itemId)) {
                            superSkuChangedItemIdsRef.current = [
                              ...superSkuChangedItemIdsRef.current,
                              clickData.itemId,
                            ];
                          }
                          pod.onChange(clickData);
                        }}
                        onHover={pod.onSSKUHover}
                        componentImpressionData={{
                          name: 'ProductPod',
                          component: 'ProductPod',
                          position: position + 1,
                          type: 'product',
                          anchorSku: parentId,
                        }}
                        isImpressionsRequired
                        hideComponentWhenEmpty
                      />
                      <ProductServiceAddOns itemId={pod.itemId} />
                    </PodTapper>
                  </PodSection>
                  {isViewSimilarButtonEnabled && (
                    <PodSection
                      display="sui-block sui-w-full"
                      noPadding
                      grid="sui-col-start-1 sui-col-end-3 md:sui-order-2"
                      order="sui-order-3"
                      columnAlign
                      zIndex
                      fitWidth
                    >
                      <div className="sui-flex sui-items-center sui-z-20 sui-mt-2">
                        <ViewSimilarIcon itemId={pod.itemId} storeId={storeId} />
                      </div>
                    </PodSection>
                  )}
                  <PodSection
                    display="sui-grid"
                    gap="sui-gap-0"
                    order="sui-order-2 md:sui-order-3"
                    padding="sui-mb-auto sui-px-1"
                    alignTop
                    noPadding
                  >
                    <PodTapper fitWidth zIndex>
                      <PodSpacer margin="md:sui-mt-1">
                        <ProductHeader
                          brand="inline"
                          brandLineHeight="tight"
                          brandFontSize="sm"
                          productNameLineHeight="tight"
                          itemId={pod.itemId}
                          brandTitleMaxLine={4}
                          onClick={onClick}
                          titleMaxLine={3}
                          disableShopThisCollection
                        />
                      </PodSpacer>
                    </PodTapper>
                    <PodTapper fitWidth zIndex>
                      <ProductRating itemId={pod.itemId} onClick={onClick} />
                    </PodTapper>
                    <ProductIdentifier itemId={pod.itemId} type="model" />
                  </PodSection>
                </PodRowToColumn>
                <PodSection noPadding alignTop columnAlign padding="sui-px-1">
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    <PodSpacer padding={['TOP', 'BOTTOM']} disable={pod.productSubType === 'HDQC'}>
                      <Price
                        clsRemediation={{ placeholders: true, preservePlaceholders: true }}
                        disableOuterSpacing
                        itemId={pod.itemId}
                        large={false}
                        storeId={storeId}
                        displayEachUom={false}
                        onSavingsCenterToggle={pod.onSavingsCenterToggle}
                        hideForProductTypes={['HDQC']}
                        showPreferredPricingBadge
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                <PodSection noPadding alignTop columnAlign padding="sui-px-0">
                  <PodTapper zIndex pointerNone aPointer buttonPointer spanPointer>
                    <PodSpacer padding={['TOP', 'BOTTOM']}>
                      <Promotion
                        itemId={pod.itemId}
                        hideConditionalPromoDescription
                        pageType={parent.toUpperCase()}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
              </PodSection>
              <PodFooter noPadding margin="sui-mt-auto">
                <PodSection display="sui-grid" padding="sui-px-1" noPadding alignEnd>
                  <PodTapper
                    zIndex
                    aPointer
                    buttonPointer
                    spanPointer
                    cursorPointer
                    handleClick={(event) => sendPodTapperAnalytics(event, pod.itemId, url)}
                  >
                    {showFulfillmentPodStore && (
                      <FulfillmentPodStore itemId={pod.itemId} storeId={storeId} />
                    )}
                    {showFulfillmentPodShipping && (
                      <FulfillmentPodShipping
                        itemId={pod.itemId}
                        storeId={storeId}
                        hasSameDayFilter={hasSameDayFilter}
                        showFasterPodSpacing
                      />
                    )}
                    {isMobileOrConsumerApp && (
                      <PodSpacer>
                        <AisleBayPod
                          itemId={pod.itemId}
                          clsRemediation={{
                            placeholders: true,
                            preservePlaceholders: false,
                          }}
                          storeId={storeId}
                          padding="sui-mb-2"
                        />
                      </PodSpacer>
                    )}
                  </PodTapper>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodSpacer padding={['TOP', 'BOTTOM']}>
                    <div className="sui-flex sui-justify-center">
                      <OrderSample
                        disableDrawerCTA
                        disableSampleCall={
                          samplesLoading
                          || (pod.itemId !== itemId
                            && !superSkuChangedItemIdsRef.current.includes(pod.itemId))
                        }
                        itemId={pod.itemId}
                        link
                        showTitle={false}
                        className="sui-z-20"
                      />
                    </div>
                  </PodSpacer>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodTapper zIndex>
                    <div className="sui-flex-1 sui-w-full">
                      <PodSpacer padding={['BOTTOM']}>
                        <ProductQuantity
                          itemId={pod.itemId}
                          onQuantityChange={pod.onQuantityUpdate}
                          mediumWidth
                          wideView
                          noRightMargin
                        />
                      </PodSpacer>
                    </div>
                  </PodTapper>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodTapper zIndex>
                    <div className="sui-flex-1 sui-w-full">
                      <PodSpacer padding={['TOP', 'BOTTOM']}>
                        <ProductATC
                          itemId={pod.itemId}
                          storeId={storeId}
                          checkGeneric
                          hasInStoreFilter={hasInStoreFilter}
                          quantity={pod.quantity}
                          variant="primary"
                          isB2BPLP
                        />
                      </PodSpacer>
                    </div>
                  </PodTapper>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodTapper zIndex>
                    <ProductSeeSimilarItems
                      itemId={pod.itemId}
                      position={itemRecsIndex}
                      productPodRef={pod.ref}
                      storeId={storeId}
                    />
                  </PodTapper>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodTapper zIndex>
                    <PodSpacer padding={['TOP', 'BOTTOM']}>
                      <NonBuyableQuoteCenter
                        itemId={pod.itemId}
                        checkForProductSubType={['HDQC']}
                      />
                    </PodSpacer>
                  </PodTapper>
                </PodSection>
                <PodSection display="sui-grid" gap="sui-gap-0" noPadding alignEnd>
                  <PodTapper zIndex>
                    <div className="sui-flex-1 sui-w-full">
                      <PodSpacer padding={['TOP', 'BOTTOM']}>
                        <AddToVariations pod={pod} storeId={storeId} />
                      </PodSpacer>
                    </div>
                  </PodTapper>
                </PodSection>
              </PodFooter>
            </div>
          </>
        )}
      />
    </div>
  );
};

CustomProductPodResponsiveB2B.displayName = 'CustomProductPodResponsiveB2B';

CustomProductPodResponsiveB2B.propTypes = {
  itemId: PropTypes.string.isRequired,
  parentId: PropTypes.string,
  itemRecsIndex: PropTypes.number.isRequired,
  storeId: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  hoverDelay: PropTypes.number.isRequired,
  onClick: PropTypes.func,
  hasInStoreFilter: PropTypes.bool.isRequired,
  samplesLoading: PropTypes.bool.isRequired,
  url: PropTypes.string.isRequired,
  impProductPodData: PropTypes.shape({}),
  parent: PropTypes.string,
  hasSameDayFilter: PropTypes.bool
};

CustomProductPodResponsiveB2B.defaultProps = {
  impProductPodData: null,
  onClick: () => {},
  parent: 'plp',
  hasSameDayFilter: false,
  parentId: null
};

CustomProductPodResponsiveB2B.dataModel = extend(
  ProductPod,
  ProductImage,
  ProductServiceAddOns,
  AddToList,
  AddToVariations,
  Price,
  Promotion,
  SuperSkuMiniSwatches,
  ProductATC,
  ProductRating,
  ProductSample,
  ProductHeader,
  ProductIdentifier,
  ProductBadge,
  ProductSponsored,
  ProductSeeSimilarItems,
  FulfillmentPodStore,
  FulfillmentPodShipping,
  NonBuyableQuoteCenter,
  OrderSample,
  PriceClearanceDataModel,
  BundleDataModel,
  ViewSimilarIcon,
);
